/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";
import { useLocation } from "../../core/location-provider";

import { gql, useLazyQuery } from "@apollo/client";
import CustomMapMarker2 from "../../components/common/custom-map-marker-2";
import LoadingSpinnerFull from "../../components/common/loading-spinner/loading-spinner-full";
import TransitionWrapper from "../../components/common/transition-wrapper";
import { Identify, identify, track } from "@amplitude/analytics-browser";
import { usePullToRefresh } from "../../core/pull-to-refresh-provider";
import { isWeb } from "../../utils/user-agent";
import AppDownloadModal from "../../components/app-download/app-download-modal"; // 새로운 모달 컴포넌트 추가

import { DEFAULT_MAP_LAT, DEFAULT_MAP_LNG } from "../../utils/map-defaults";
import {
  MAP_TAB_DEFAULT_ZOOM,
  setMapPosition,
} from "../maps/utils/map-position-storage";
import { useRequestCurrentLocation } from "../../hooks/use-request-current-location";
import LocationButton from "../../components/common/location-button";

const GET_LOCAL_ADDRESS = gql`
  query GetLocalAddress($lat: Float!, $lng: Float!, $pushToken: String) {
    getLocalAddressV2(lat: $lat, lng: $lng, push_token: $pushToken) {
      loadAddr
      area1
      area2
      area3
      area4
    }
  }
`;

function Location() {
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 상태 추가
  const { hasLastLo, getLocationFromStorage, setLocationToStorage } =
    useLocation();
  const [getLocalAddress] = useLazyQuery(GET_LOCAL_ADDRESS);
  const navigate = useNavigate();
  const mapElement = useRef<HTMLDivElement | null>(null);
  const { setIsPullable } = usePullToRefresh();
  const { naver } = window;
  let map: naver.maps.Map;
  // 지도가 이동하였을 때 가려진 부분의 마커는 숨기고
  // 노출된 부분의 마커를 표시하는 작업을 하기 위해 map 인스턴스를 state로 관리
  const [newMap, setNewMap] = useState<naver.maps.Map | null>(null);
  const [newMarker, setNewMarker] = useState<naver.maps.Marker | null>(null);

  const [currentLat, setCurrentLat] = useState(0);
  const [currentLng, setCurrentLng] = useState(0);
  // [지도를 그리는 effect]
  useEffect(() => {
    if (!mapElement.current || !naver) {
      return;
    }
    // Map 클래스는 지도를 표현하는 클래스
    // new 연산자를 이용하여 새 인스턴스를 생성
    // 변환해놓은 좌표값을 이용하여 지도 중심 인스턴스 생성
    const center = hasLastLo
      ? new naver.maps.LatLng(
          getLocationFromStorage().lat,
          getLocationFromStorage().lng,
        )
      : new naver.maps.LatLng(DEFAULT_MAP_LAT, DEFAULT_MAP_LNG);
    // 지도 옵션 설정둪
    const mapOptions: naver.maps.MapOptions = {
      center,
      zoom: MAP_TAB_DEFAULT_ZOOM,
      minZoom: 7,
      maxZoom: 19,
      zoomControl: true,
      zoomControlOptions: {
        style: naver.maps.ZoomControlStyle.SMALL,
        position: naver.maps.Position.TOP_RIGHT,
      },
      mapDataControl: false,
      scaleControl: false,
    };

    //설정해놓은 옵션을 바탕으로 지도 생성
    map = new naver.maps.Map(mapElement.current, mapOptions);
    setNewMap(map);
    //마커 생성 함수 호출
    const marker = new naver.maps.Marker({
      position: center,
      map,
      clickable: true,
      // [마커 커스터마이징]
      icon: {
        //html element를 반환하는 CustomMapMarker 컴포넌트 할당
        content: CustomMapMarker2(),
        //마커의 기준위치 지정
        anchor: new naver.maps.Point(25, 58),
      },
    });
    setNewMarker(marker);

    // [마커는 항상 맵 중앙에]
    naver.maps.Event.addListener(map, "drag", function (e) {
      const centerCoord = map.getCenter();
      marker.setPosition(centerCoord);
    });
  }, []);

  useEffect(() => {
    setIsPullable(false);

    return () => {
      setIsPullable(true);
    };
  }, []);
  const requestCurrentLocation = useRequestCurrentLocation();

  const clickButton = () => {
    if (isWeb()) {
      setIsModalOpen(true);
      return;
    }
    setLoading(true);
    track("내위치 버튼 클릭");
    requestCurrentLocation((coords) => {
      setCurrentLat(coords.latitude);
      setCurrentLng(coords.longitude);

      newMap?.setZoom(16);
      newMap?.panTo(new naver.maps.LatLng(coords.latitude, coords.longitude), {
        duration: 0,
      });

      newMarker?.setPosition(
        new naver.maps.LatLng(coords.latitude, coords.longitude),
      );
      newMarker?.setMap(newMap ?? null);
      setLoading(false);
    });
  };

  async function onComplete() {
    if (!newMap) return;

    const center = newMap.getCenter();
    await getLocalAddress({
      variables: {
        lat: center.y,
        lng: center.x,
        pushToken: localStorage.getItem("expo_push_token"),
        // responseFormat: "object",
      },
      onCompleted: (data) => {
        if (data.getLocalAddressV2) {
          localStorage.setItem("loadAddr", data.getLocalAddressV2.loadAddr);
          track("위치 적용 버튼 클릭", {
            어디로: data.getLocalAddressV2.loadAddr,
          });
          // Amplitude Identify 객체를 사용하여 사용자 속성 업데이트
          const identifyUser = new Identify().set(
            "loadAddr",
            data.getLocalAddressV2.loadAddr,
          );
          identify(identifyUser);
        }
      },
      onError: () => localStorage.setItem("loadAddr", ""),
    });

    setLocationToStorage(center.y, center.x);
    setMapPosition({
      lat: center.y,
      lng: center.x,
      zoom: MAP_TAB_DEFAULT_ZOOM,
    });
    navigate("/");
  }
  const mapHeight = window.innerHeight - 92; // 1. 위치설정 바 2. 탭 3. 바텀탭
  return (
    <div className="w-[100%] h-full">
      {loading ? <LoadingSpinnerFull /> : null}
      <div
        ref={mapElement}
        id="map"
        style={{ width: "100%", height: mapHeight }}
      >
        <LocationButton
          onClick={clickButton}
          variant="withTransition"
          className="absolute bottom-10 right-6 z-50"
        />
      </div>

      {/* 새로운 모달 컴포넌트 추가 */}
      <AppDownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        where="위치 설정 내 내위치 찾기 버튼"
      />

      <div>{currentLng ? currentLat : null}</div>
      <div>{currentLng ? currentLng : null}</div>
      <div
        onClick={() => onComplete()}
        className="max-w-[742px] px-5 py-3 w-full fixed bottom-0 z-50 bg-white rounded-tl-[10px] rounded-tr-[10px]"
      >
        <div className="absolute left-0 top-[-2.2rem] w-full h-[36px] bg-[#282828] rounded-tl-[10px] rounded-tr-[10px] text-white text-base font-semibold flex justify-center items-center">
          5km 내 매장을 찾아드려요!
        </div>
        <TransitionWrapper
          scale={0.95}
          opacity={0.8}
          className=" w-full h-[60px] flex items-center justify-center bg-[#1562fc] rounded-lg border"
        >
          {/* Button Content */}
          <div className="text-center flex items-center space-x-1">
            <span className="text-white text-xl font-bold leading-snug">
              완료
            </span>
          </div>
        </TransitionWrapper>
      </div>
    </div>
  );
}

export default Location;
